import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import {
  Beaker,
  Zap,
  RefreshCw,
  Copy,
  CheckCircle2,
  ChevronRight,
  Clock,
  FileText,
  Sparkles,
  History,
  AlertTriangle,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const PROMPT_ICONS = {
  wall_street_evaluation: "📊",
  earnings_summary:       "📈",
  compare_stocks:         "⚖️",
  youtube_cheat_sheet:    "🎬",
  undervalued_sectors:    "🔍",
  risk_management:        "🛡️",
  weekly_routine:         "📅",
  trade_with_precision:   "🎯",
};

function Markdownish({ text }) {
  // Lightweight markdown rendering — bold, headers, lists, tables.
  if (!text) return null;
  const lines = text.split("\n");
  const out = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc list-inside space-y-1 text-sm text-zinc-200 my-2">
          {listBuf.map((it, i) => <li key={i} dangerouslySetInnerHTML={{ __html: it }} />)}
        </ul>
      );
      listBuf = [];
    }
  };
  const renderInline = (s) => {
    let h = s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    h = h.replace(/\*\*([^*]+)\*\*/g, '<strong class="text-white">$1</strong>');
    h = h.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 bg-zinc-800 rounded text-emerald-300">$1</code>');
    return h;
  };
  for (const raw of lines) {
    const line = raw.replace(/\r$/, "");
    if (/^#{1,2}\s/.test(line)) {
      flushList();
      const level = line.match(/^#+/)[0].length;
      const txt = line.replace(/^#+\s*/, "");
      out.push(level === 1
        ? <h3 key={`h-${out.length}`} className="text-base font-bold text-white mt-3 mb-1">{txt}</h3>
        : <h4 key={`h-${out.length}`} className="text-sm font-bold text-emerald-300 mt-2 mb-1 uppercase tracking-wider">{txt}</h4>);
    } else if (/^[-*]\s+/.test(line)) {
      listBuf.push(renderInline(line.replace(/^[-*]\s+/, "")));
    } else if (line.trim() === "") {
      flushList();
    } else {
      flushList();
      out.push(<p key={`p-${out.length}`} className="text-sm text-zinc-200 leading-relaxed my-1.5"
                   dangerouslySetInnerHTML={{ __html: renderInline(line) }} />);
    }
  }
  flushList();
  return <div>{out}</div>;
}

function PromptCard({ prompt, onRun, isRunning, hasResult }) {
  return (
    <button
      data-testid={`experimental-prompt-${prompt.id}`}
      onClick={onRun}
      disabled={isRunning}
      className={`w-full text-left rounded-lg border p-3 transition-all group ${
        hasResult
          ? "bg-emerald-500/5 border-emerald-400/30 hover:border-emerald-400/60"
          : "bg-black/40 border-zinc-800 hover:border-emerald-400/40 hover:bg-emerald-500/5"
      } disabled:opacity-50`}
    >
      <div className="flex items-start gap-3">
        <div className="text-2xl shrink-0">{PROMPT_ICONS[prompt.id] || "🧪"}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-bold text-white">{prompt.label}</span>
            {hasResult && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{prompt.description}</p>
        </div>
        <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-emerald-300 shrink-0 mt-1" />
      </div>
    </button>
  );
}

function InputPanel({ prompt, values, onChange, onSubmit, onCancel, running }) {
  if (!prompt) return null;
  return (
    <Card className="card-dark border-emerald-400/30">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{PROMPT_ICONS[prompt.id] || "🧪"}</span>
            <div>
              <h3 className="text-base font-bold text-white">{prompt.label}</h3>
              <p className="text-[11px] text-muted-foreground">{prompt.description}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onCancel} className="h-7 text-xs">Cancel</Button>
        </div>

        {prompt.inputs.length === 0 && (
          <p className="text-xs text-muted-foreground italic">No inputs needed — just hit Run.</p>
        )}

        {prompt.inputs.map((inp) => (
          <div key={inp.name} className="space-y-1">
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">
              {inp.label}{inp.required && <span className="text-rose-400 ml-1">*</span>}
            </label>
            {inp.type === "textarea" ? (
              <Textarea
                data-testid={`experimental-input-${inp.name}`}
                value={values[inp.name] || ""}
                onChange={(e) => onChange(inp.name, e.target.value)}
                rows={inp.name === "url_or_transcript" ? 5 : 3}
                className="bg-black/40 border-zinc-800 text-white text-sm"
                placeholder={inp.label}
              />
            ) : (
              <Input
                data-testid={`experimental-input-${inp.name}`}
                value={values[inp.name] || ""}
                onChange={(e) => onChange(inp.name, e.target.value)}
                className="bg-black/40 border-zinc-800 text-white"
                placeholder={inp.label}
              />
            )}
          </div>
        ))}

        <Button
          data-testid="experimental-submit"
          onClick={onSubmit}
          disabled={running}
          className="w-full h-10 bg-emerald-600 hover:bg-emerald-500 text-white"
        >
          {running
            ? <><RefreshCw className="w-4 h-4 animate-spin mr-2" />Analyzing… up to 30s</>
            : <><Zap className="w-4 h-4 mr-2" />Run Analysis</>}
        </Button>
      </CardContent>
    </Card>
  );
}

function ResultPanel({ result, onClear, onCopy }) {
  if (!result) return null;
  return (
    <Card className="card-dark border-border" data-testid="experimental-result">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2 border-b border-zinc-800 pb-2">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-emerald-300" />
            <span className="text-sm font-bold text-white">{result.label}</span>
            {result.cost_usd != null && (
              <Badge className="bg-zinc-800 text-zinc-400 border-0 text-[10px]">
                ${result.cost_usd.toFixed(4)}
              </Badge>
            )}
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={onCopy} className="h-7 px-2 text-xs">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy
            </Button>
            <Button size="sm" variant="ghost" onClick={onClear} className="h-7 px-2 text-xs">
              Clear
            </Button>
          </div>
        </div>
        <div className="prose prose-invert max-w-none">
          <Markdownish text={result.answer} />
        </div>
      </CardContent>
    </Card>
  );
}

export default function ExperimentalWork() {
  const [prompts, setPrompts] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePrompt, setActivePrompt] = useState(null);
  const [values, setValues] = useState({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const fetchPrompts = useCallback(async () => {
    setLoading(true);
    try {
      const [pr, hr] = await Promise.all([
        api.get("/experimental/prompts"),
        api.get("/experimental/history?limit=5").catch(() => ({ data: { runs: [] } })),
      ]);
      setPrompts(pr.data?.prompts || []);
      setHistory(hr.data?.runs || []);
    } catch {
      toast.error("Failed to load prompts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPrompts(); }, [fetchPrompts]);

  const openPrompt = useCallback((p) => {
    setActivePrompt(p);
    setValues({});
    setResult(null);
    // If prompt has no inputs, run immediately
    if ((p.inputs || []).length === 0) setTimeout(() => submit(p, {}), 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (p = activePrompt, v = values) => {
    if (!p) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post("/experimental/run", {
        prompt_id: p.id,
        variables: v,
      });
      setResult(r.data);
      toast.success(`${p.label} ready`);
      // refresh history
      api.get("/experimental/history?limit=5").then((hr) => setHistory(hr.data?.runs || [])).catch(() => {});
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail || "Run failed. Check inputs and try again.");
    } finally {
      setRunning(false);
    }
  };

  const copyToClipboard = async () => {
    if (!result?.answer) return;
    try {
      await navigator.clipboard.writeText(result.answer);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="space-y-4 pb-24" data-testid="experimental-work-page">
      <Card className="card-dark border-border">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-fuchsia-500/10">
              <Beaker className="w-5 h-5 text-fuchsia-300" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
                Experimental Work
                <Badge className="bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-400/40 border text-[10px]">BETA</Badge>
              </h1>
              <p className="text-sm text-muted-foreground mt-1 italic">
                &ldquo;No distractions. No panic. Just clean info, smart tactics, and clearer decisions.&rdquo;
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                <Sparkles className="w-3 h-3 inline mr-1 text-fuchsia-300" />
                8 curated prompts to research, analyze, and act confidently. Powered by Claude Sonnet 4.5.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {activePrompt && (
        <InputPanel
          prompt={activePrompt}
          values={values}
          onChange={(k, v) => setValues((cur) => ({ ...cur, [k]: v }))}
          onSubmit={() => submit()}
          onCancel={() => { setActivePrompt(null); setResult(null); setValues({}); }}
          running={running}
        />
      )}

      <ResultPanel
        result={result}
        onClear={() => setResult(null)}
        onCopy={copyToClipboard}
      />

      {!activePrompt && (
        <Card className="card-dark border-border">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground">
                Choose your prompt
              </h3>
              <Button size="sm" variant="ghost" onClick={fetchPrompts} disabled={loading} className="h-7">
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {prompts.map((p) => (
                <PromptCard
                  key={p.id}
                  prompt={p}
                  onRun={() => openPrompt(p)}
                  isRunning={running}
                  hasResult={history.some((h) => h.prompt_id === p.id && h.ok)}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {history.length > 0 && (
        <Card className="card-dark border-border">
          <CardContent className="p-4 space-y-2">
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <History className="w-3.5 h-3.5" /> Recent runs
            </h3>
            <div className="space-y-1.5">
              {history.map((h, i) => (
                <button
                  key={i}
                  onClick={() => setResult({ label: h.label, answer: h.answer, cost_usd: h.cost_usd })}
                  className="w-full text-left flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-zinc-800/50 text-xs"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span>{PROMPT_ICONS[h.prompt_id] || "🧪"}</span>
                    <span className="text-zinc-200 truncate">{h.label}</span>
                    {!h.ok && <AlertTriangle className="w-3 h-3 text-rose-400" />}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {h.cost_usd != null && (
                      <span className="text-[10px] text-zinc-500">${h.cost_usd.toFixed(4)}</span>
                    )}
                    <Clock className="w-3 h-3 text-zinc-500" />
                    <span className="text-[10px] text-zinc-500">
                      {new Date(h.started_at).toLocaleString()}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
